import AdStatusButtons from '../../components/AdStatusButtons';
import AdStatusToggle from '../../components/AdStatusToggle';
import { useAdStatus, useMayChangeStatus, NOUN } from '../../components/ad-status';
import { resolveSelectedIds } from '../../utils/entity-list';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsService, Campaign } from '../../services/campaigns.service';
import { useServerPagination } from '../../hooks/useServerPagination';
import StatusBadge from '../../components/StatusBadge';
import EntityListFilters, { type EntityFilters } from '../../components/EntityListFilters';

export default function CampaignsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { gridProps, offset, limit, resetPage } = useServerPagination(100);
  const [filters, setFilters] = useState<EntityFilters>({ search: '', status: '', adAccountId: '' });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCampaign, setMenuCampaign] = useState<Campaign | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['campaigns', offset, limit, filters],
    queryFn: () => campaignsService.query({
      offset,
      limit,
      ...(filters.search && { search: filters.search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.adAccountId && { ad_account_id: filters.adAccountId }),
    }),
    placeholderData: (prev) => prev,
  });

  // Reset to first page when filters change
  const handleFiltersChange = (next: EntityFilters) => {
    setFilters(next);
    resetPage();
  };

  const deleteMutation = useMutation({
    mutationFn: campaignsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: campaignsService.duplicate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });



  const mayChangeStatus = useMayChangeStatus();
  const rowStatus = useAdStatus('campaign', 'campaigns');

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Campaign Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ad_account',
      headerName: 'Ad Account',
      width: 180,
      valueGetter: (_value, row) => row.ad_account?.name || '-',
    },
    {
      field: 'objective',
      headerName: 'Objective',
      width: 150,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StatusBadge
            status={params.value}
            effectiveStatus={params.row.effective_status}
            staleSince={params.row.status_stale_since}
          />
          {mayChangeStatus && (
            <AdStatusToggle
              status={params.value}
              noun={NOUN['campaign'][0]}
              onToggle={(next) => rowStatus.ask([String(params.row.id)], next)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'fb_campaign_id',
      headerName: 'FB ID',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return null;
        return (
          <Tooltip title="Click to copy">
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(params.value); }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'daily_budget',
      headerName: 'Daily Budget',
      width: 130,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'lifetime_budget',
      headerName: 'Lifetime Budget',
      width: 140,
      valueFormatter: (value) =>
        value ? `$${Number(value).toFixed(2)}` : '-',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuCampaign(params.row);
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const rows = data?.data || [];
  const selectedIds = resolveSelectedIds(selectedRows, rows);
  const totalsRow = useMemo(() => {
    if (rows.length === 0) return null;
    return {
      id: '__totals__',
      name: `Total (${data?.total || rows.length})`,
      ad_account: null,
      objective: '',
      status: '',
      fb_campaign_id: null,
      daily_budget: rows.reduce((s, r) => s + (r.daily_budget || 0), 0) || null,
      lifetime_budget: rows.reduce((s, r) => s + (r.lifetime_budget || 0), 0) || null,
      created_at: '',
    };
  }, [rows, data?.total]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Campaigns
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedIds.length > 0 && (
            <AdStatusButtons
              entityType="campaign"
              ids={selectedIds}
              invalidateKey="campaigns"
              alreadyInState={(status) => selectedIds.filter(
                (id) => rows.find((r) => String(r.id) === id)?.status === status).length}
              onDone={() => setSelectedRows({ type: 'include', ids: new Set() })}
            />
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/campaigns/new')}
          >
            Create Campaign
          </Button>
        </Box>
      </Box>

      <EntityListFilters filters={filters} onFiltersChange={handleFiltersChange} />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        disableRowSelectionOnClick
        pinnedRows={totalsRow ? { top: [totalsRow] } : undefined}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          height: 'calc(100vh - 280px)',
          minHeight: 400,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'action.hover',
            fontWeight: 600,
          },
        }}
        onRowClick={(params: GridRowParams) => {
          if (params.id === '__totals__') return;
          navigate(`/campaigns/${params.id}`);
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            navigate(`/campaigns/${menuCampaign?.id}/edit`);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuCampaign) {
              duplicateMutation.mutate(menuCampaign.id);
            }
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuCampaign) {
              deleteMutation.mutate(menuCampaign.id);
            }
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {rowStatus.dialogs}
    </Box>
  );
}
